import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuthContext } from "../contexts/AuthContext";
import { Activity } from "../types/types";
import { Backdrop, Button, CircularProgress, Container, FormControl, FormHelperText, Grid, InputLabel, MenuItem, Select, SelectChangeEvent, TextField, Typography } from "@mui/material";
import { getApiUrlFromStorage, getFormattedDateTime, getTokenFromStorage } from "../helpers/helpers";
import ActivityHeader from "./ActivityHeader";

const ActivityDetails = ({ mode }: { mode: 'add' | 'edit' }) => {

  const { id } = useParams();
  const [isLoading, setIsLoading] = useState(false);
  const { user, activityTypes } = useAuthContext();
  const [activityType, setActivityType] = useState("");
  const [notes, setNotes] = useState("");
  const [startTime, setStartTime] = useState<string | null>(null);
  const [endTime, setEndTime] = useState<string | null>(null);
  const [missingActivityType, setMissingActivityType] = useState(false);

  const navigate = useNavigate();

  const handleActivityTypeChange = (event: SelectChangeEvent) => {
    setActivityType(event.target.value);
  };

  const apiUrl = getApiUrlFromStorage();
  const token = getTokenFromStorage();

  useEffect(() => {
    if (id && apiUrl && token) {
      setIsLoading(true);

      fetch(`${apiUrl}/activities/${id}?populate=activity_type`, {
        headers: {
          'Content-type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
      })
        .then(res => {
          if (!res.ok) {
            throw Error(`Unable to login. Status: ${res.status} - ${res.statusText}`);
          }
          return res.json();
        })
        .then((data) => {
          const activity: Activity = data.data;
          setActivityType(activity.attributes.activity_type.data.id.toString());
          setStartTime(activity.attributes.start_time);
          setEndTime(activity.attributes.end_time);
          setNotes(activity.attributes.notes ?? "");
        })
        .catch((err) => {
          // catches network errors + http not OK errors
          console.log(err);
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [id, apiUrl, token]);

  const handleAddActivity = (e: React.MouseEvent) => {
    e.preventDefault();
    setMissingActivityType(false);

    if (activityType === '') {
      setMissingActivityType(true);
      return;
    }

    setIsLoading(true);

    const body = {
      data: {
        'activity_type': activityType,
        'notes': notes,
        'start_time': Date.now()
      }
    }

    fetch(`${apiUrl}/activities`, {
      method: 'POST',
      headers: {
        'Content-type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(body)
    })
      .then(res => {
        if (!res.ok) {
          throw Error(`Unable to submit activity. Status: ${res.status} - ${res.statusText}`);
        }
        return res.json();
      })
      .then((data) => {
        navigate('/activity-list');
      })
      .catch((err) => {
        // catches network errors + http not OK errors
        console.log(err);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleEditActivity = (e: React.MouseEvent, saveNotesOnly = false) => {
    e.preventDefault();
    setIsLoading(true);

    let body = {
      data: {
        'notes': notes,
      }
    };

    if (!saveNotesOnly) {
      let dataObj = { ...body.data, 'end_time': Date.now() };
      body.data = dataObj;
    }

    fetch(`${apiUrl}/activities/${id}`, {
      method: 'PUT',
      headers: {
        'Content-type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(body)
    })
      .then(res => {
        if (!res.ok) {
          throw Error(`Unable to edit activity. Status: ${res.status} - ${res.statusText}`);
        }
        return res.json();
      })
      .then((data) => {
        navigate('/activity-list');
      })
      .catch((err) => {
        // catches network errors + http not OK errors
        console.log(err);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleDeleteActivity = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsLoading(true);

    fetch(`${apiUrl}/activities/${id}`, {
      method: 'DELETE',
      headers: {
        'Content-type': 'application/json',
        'Authorization': `Bearer ${token}`,
      }
    })
      .then(res => {
        if (!res.ok) {
          throw Error(`Unable to delete activity. Status: ${res.status} - ${res.statusText}`);
        }
        return res.json();
      })
      .then((data) => {
        navigate('/activity-list');
      })
      .catch((err) => {
        // catches network errors + http not OK errors
        console.log(err);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const getSubmitButton = (): JSX.Element => {
    if (!startTime) {
      return <Button sx={{ mt: 2 }} variant="contained" onClick={handleAddActivity}>Start</Button>
    }
    else if (!endTime) {
      return <Button sx={{ mt: 2 }} variant="contained" onClick={handleEditActivity}>End</Button>
    }
    else {
      return <Button sx={{ mt: 2 }} variant="contained" onClick={(e) => handleEditActivity(e, true)}>Save</Button>
    }
  }

  const getDeleteButton = (): JSX.Element => {
    if (mode === 'edit') {
      return <Button sx={{ mt: 2, ml: 1 }} variant="contained" color="error" onClick={handleDeleteActivity}>Delete</Button>
    }
    return <></>
  }

  return (
    <>
      <ActivityHeader user={user} />
      {
        <div>
          <Backdrop
            sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
            open={isLoading}
          >
            <CircularProgress color="inherit" />
          </Backdrop>
        </div>
      }
      <form noValidate>
        <Container sx={{ mb: 3 }}>
          <FormControl error={missingActivityType} variant="standard" sx={{ mt: 1 }} fullWidth>
            <InputLabel id="activity-type-label">Type of Activity</InputLabel>
            <Select
              readOnly={!!startTime}
              labelId="activity-type-label"
              id="activity-type-select"
              value={activityType}
              label="ActivityType"
              onChange={handleActivityTypeChange}
            >
              <MenuItem value="">
                <em>Please pick:</em>
              </MenuItem>
              {
                activityTypes?.map((activityType) => {
                  return <MenuItem key={activityType.id} value={activityType.id}>{activityType.attributes.name}</MenuItem>;
                })
              }
            </Select>
            <FormHelperText>{missingActivityType ? 'Please select a type' : ''}</FormHelperText>
          </FormControl>
        </Container>
        <Container sx={{ mb: 4 }}>
          <Grid container spacing={2}>
            {
              startTime ? <Grid item sm={6} xs={12} sx={{ display: 'flex' }}>
                <Typography variant="body1" color="text.secondary">Start time:</Typography>&nbsp;
                <Typography variant="body1">{getFormattedDateTime(startTime)}</Typography>
              </Grid> : <></>
            }
            {
              endTime ? <Grid item sm={6} xs={12} sx={{ display: 'flex' }}>
                <Typography variant="body1" color="text.secondary">End time:</Typography>&nbsp;
                <Typography variant="body1">{getFormattedDateTime(endTime)}</Typography>
              </Grid> : <></>
            }

          </Grid>
        </Container>
        <Container>
          <TextField
            onChange={(e) => setNotes(e.target.value)}
            fullWidth
            id="notes"
            label="Notes"
            multiline
            rows={4}
            value={notes}
          />
        </Container>
        <Container>
          {getSubmitButton()}
          {getDeleteButton()}
        </Container>
      </form>
    </>
  );
}

export default ActivityDetails;